import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Flex,
  Group,
  Paper,
  SimpleGrid,
  Text,
  ThemeIcon,
  useMantineTheme,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import React, { useEffect, useState } from 'react';
import { api } from '../../services/master.service';
import { showErrorToast, showSuccessToast } from '../../utilities/Toast';
import ViewOrderModal from '../../components/ViewOrderModal';
import moment from 'moment';
import {
  IconBriefcase,
  IconEdit,
  IconHome2,
  IconMap2,
  IconMapPin,
  IconMinus,
  IconPhone,
  IconPlus,
  IconTrash,
  IconTruckDelivery,
  IconUser,
} from '@tabler/icons-react';
import AddAddressModal from '../../components/AddAddressModal';
import { is_mobile_view } from '../../config';

const getAddressIcon = (title = '') => {
  const value = title.toLowerCase();
  if (value.includes('office') || value.includes('work')) return IconBriefcase;
  if (value.includes('home')) return IconHome2;
  return IconMapPin;
};

function Addresses() {
  let isMobileScreen = useMediaQuery('(max-width: 768px)');
  isMobileScreen = is_mobile_view ? is_mobile_view : isMobileScreen;
  const theme = useMantineTheme();

  const [viewModalOpened, setViewModalOpened] = useState(false);
  const [addressModalOpened, setAddressModalOpened] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});

  const [addresses, setAddresses] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    api.get_addresses().then((res) => {
      console.log(res.data.data);
      setAddresses(res.data.data);
    });
  }, []);

  const getData = () => {
    api.get_addresses().then((res) => {
      console.log(res.data.data);
      setAddresses(res.data.data);
    });
  };

  const deleteAddress = async (id) => {
    await api.delete_address(id).then((res) => {
      setIsLoading(false);
      console.log(res.data);
      if (res.data.success) {
        showSuccessToast({
          title: 'Success',
          message: 'Successfully deleted address.',
        });
        getData();
      } else {
        showErrorToast({
          title: 'Error',
          message: 'Error deleting address.',
        });
      }
    });
  };

  return (
    <Box mt={0}>
      <Paper
        radius={22}
        p={isMobileScreen ? 16 : 24}
        mb={isMobileScreen ? 24 : 30}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${theme.colors.green[0]}, ${theme.white} 56%, ${theme.colors.gray[0]})`,
          border: `1px solid ${theme.colors.green[1]}`,
          boxShadow: '0 18px 42px rgba(13, 71, 21, 0.08)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            right: -46,
            top: -72,
            width: 180,
            height: 180,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(34,197,94,.18), transparent 68%)',
          }}
        />
        <Flex
          justify="space-between"
          align={isMobileScreen ? 'flex-start' : 'center'}
          direction={isMobileScreen ? 'column' : 'row'}
          gap={14}
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <Group spacing={12} noWrap={!isMobileScreen}>
            <ThemeIcon
              size={54}
              radius={16}
              sx={{
                background: `linear-gradient(135deg, ${theme.colors.brand[7]}, ${theme.colors.brand[5]})`,
                boxShadow: '0 12px 24px rgba(13, 71, 21, 0.18)',
              }}
            >
              <IconTruckDelivery size={27} />
            </ThemeIcon>
            <Box>
              <Text fw={900} size={isMobileScreen ? 22 : 26} color="dark" lh={1.1}>
                Saved Addresses
              </Text>
              <Text size="sm" color="dimmed" mt={5}>
                Manage delivery locations for faster checkout.
              </Text>
            </Box>
          </Group>

          <Button
            radius="xl"
            leftIcon={<IconPlus size={16} />}
            fullWidth={isMobileScreen}
            onClick={() => {
              setAddressModalOpened(true);
              setIsEditing(false);
              setEditData({});
            }}
            sx={{
              height: 42,
              paddingInline: 20,
              fontWeight: 800,
              background: `linear-gradient(135deg, ${theme.colors.brand[7]}, ${theme.colors.brand[5]})`,
              boxShadow: '0 14px 28px rgba(13, 71, 21, 0.18)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 18px 36px rgba(13, 71, 21, 0.24)',
              },
            }}
          >
            Add Address
          </Button>
        </Flex>
      </Paper>

      {addresses.length === 0 ? (
        <Paper
          radius={24}
          p={isMobileScreen ? 24 : 36}
          sx={{
            textAlign: 'center',
            background: 'linear-gradient(145deg, rgba(255,255,255,.98), rgba(248,250,252,.92))',
            border: `1px solid ${theme.colors.gray[2]}`,
            boxShadow: '0 16px 38px rgba(15, 23, 42, 0.05)',
          }}
        >
          <ThemeIcon size={82} radius={24} variant="light" color="green" mx="auto">
            <IconMap2 size={40} />
          </ThemeIcon>
          <Text fw={900} size={22} mt={18} color="dark">
            No saved addresses yet
          </Text>
          <Text size="sm" color="dimmed" mt={8} maw={460} mx="auto">
            Add your first delivery location and make future orders faster.
          </Text>
          <Button
            mt={22}
            radius="xl"
            leftIcon={<IconPlus size={16} />}
            onClick={() => {
              setAddressModalOpened(true);
              setIsEditing(false);
              setEditData({});
            }}
          >
            Add New Address
          </Button>
        </Paper>
      ) : (
        <SimpleGrid cols={isMobileScreen ? 1 : 3} spacing={14}>
          {addresses.map((e) => {
            const AddressIcon = getAddressIcon(e.address_title);

            return (
              <Paper
                key={e.id}
                radius={22}
                p={14}
                withBorder
                sx={{
                  minHeight: isMobileScreen ? 180 : 220,
                  aspectRatio: 'auto',
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  background: 'linear-gradient(145deg, rgba(255,255,255,.99), rgba(248,250,252,.95))',
                  borderColor: theme.colors.gray[2],
                  boxShadow: '0 14px 34px rgba(15, 23, 42, 0.06)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: 0,
                    height: 5,
                    background: `linear-gradient(90deg, ${theme.colors.brand[7]}, ${theme.colors.brand[4]}, ${theme.colors.green[1]})`,
                  },
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    borderColor: theme.colors.green[2],
                    boxShadow: '0 20px 44px rgba(13, 71, 21, 0.10)',
                  },
                }}
              >
                <Flex justify="space-between" align="flex-start" gap={10} mb={12}>
                  <Group spacing={10} noWrap>
                    <ThemeIcon size={38} radius={13} variant="light" color="green">
                      <AddressIcon size={19} />
                    </ThemeIcon>
                    <Box>
                      <Badge
                        radius="xl"
                        variant="light"
                        color="green"
                        sx={{ textTransform: 'none', fontWeight: 900 }}
                      >
                        {e.address_title || 'Address'}
                      </Badge>
                    </Box>
                  </Group>

                  <Group spacing={6} noWrap>
                    <ActionIcon
                      radius="xl"
                      size={30}
                      variant="light"
                      color="green"
                      onClick={() => {
                        setAddressModalOpened(true);
                        setIsEditing(true);
                        setEditData(e);
                      }}
                    >
                      <IconEdit size={15} />
                    </ActionIcon>
                    <ActionIcon
                      radius="xl"
                      size={30}
                      variant="light"
                      color="red"
                      onClick={() => {
                        deleteAddress(e.id);
                      }}
                    >
                      <IconTrash size={15} />
                    </ActionIcon>
                  </Group>
                </Flex>

                <Box sx={{ minHeight: 0, flex: 1 }}>
                  <Group spacing={8} mb={6} noWrap>
                    <IconUser size={15} color={theme.colors.brand[7]} />
                    <Text fw={800} size="sm" color="dark">
                      {e.first_name || 'Recipient'}.
                    </Text>
                  </Group>

                  <Group spacing={8} mb={7} noWrap>
                    <IconPhone size={15} color={theme.colors.gray[6]} />
                    <Text size="sm" color="dimmed" fw={650}>
                      {e.phone_no || 'Phone not added'}
                    </Text>
                  </Group>

                  <Flex align="flex-start" gap={8}>
                    <IconMapPin size={16} color={theme.colors.brand[7]} style={{ marginTop: 2, flex: '0 0 auto' }} />
                    <Box>
                      <Text size="sm" color="dark" lh={1.42} lineClamp={2}>
                        {(e.address_line || '').toString()}
                      </Text>
                      {e.address_line_2 && (
                        <Text size="sm" color="dark" lh={1.42} lineClamp={1}>
                          {e.address_line_2}
                        </Text>
                      )}
                      <Text size="sm" color="dark" lh={1.42} lineClamp={2}>
                        {e.city}, {e.state}, {e.country}, {e.pincode}.
                      </Text>
                    </Box>
                  </Flex>
                </Box>
              </Paper>
            );
          })}
        </SimpleGrid>
      )}

      {addressModalOpened && (
        <AddAddressModal
          opened={addressModalOpened}
          isEditing={isEditing}
          editData={editData}
          onClose={() => {
            setAddressModalOpened(false);
            getData();
          }}
        />
      )}
    </Box>
  );
}

export default Addresses;
